/**
 * "Tellimused" (the staff CRM's subscriptions screen) - who is on which
 * package, across every client site.
 *
 * Was sixteen lines that rendered the shared `@wa/ui` `SubscriptionsTable`: a
 * bordered grey table with shadcn badges, a Radix dialog and the shared loader,
 * dropped into a page whose head, search field and cards had already moved to
 * the CRM's own vocabulary. The DATA has not moved - `useSubscriptions` and
 * `useAssignForm` are the same hooks web_admin renders - only the chrome has.
 *
 * Copy comes from the shared `makeT`, so a wording change lands on both apps at
 * once and neither screen drifts into its own dialect of the same nouns.
 */
import { useEffect, useRef, useState } from "react";
import {
  SUBSCRIPTION_STATUSES,
  fill,
  localeText,
  makeT,
  useSubscriptions,
  type Locale,
  type SubscriptionRow,
} from "@wa/ui";
import { useI18n } from "../../../i18n/I18nProvider";
import { api } from "../../../lib/api";
import type { StaffUser } from "../../../lib/auth";
import { MenuField, PageHead, SearchField, Toolbar } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { AssignModal } from "./AssignModal";
import { StripeStrip } from "./StripeStrip";
import { SubsList } from "./SubsList";
import { SubsStats } from "./SubsStats";

/** How long the search waits after the last keystroke before it asks the API. */
const SEARCH_DELAY = 350;

export function SubscriptionsSection(_props: { user: StaffUser }) {
  const { locale } = useI18n();
  const loc: Locale = locale === "en" ? "en" : "et";
  const t = makeT(loc);

  const {
    stats, rows, loading,
    q, setQ, planFilter, setPlanFilter, statusFilter, setStatusFilter, planOptions,
    loadList, loadStats,
    stripe, syncing, syncMsg, syncStripe,
    refunding, setRefunding, doRefund,
  } = useSubscriptions({ api, locale: loc });

  const [assigning, setAssigning] = useState<SubscriptionRow | null>(null);

  // The hook applies `q` only when the chrome asks it to - the admin table had
  // a form you pressed Enter on. Every other list in this CRM searches as you
  // type, so this one does too, on a delay rather than a request per keystroke.
  // The first run is skipped because the hook already loads on mount, and two
  // requests for the same empty query is one too many.
  const searchArmed = useRef(false);
  useEffect(() => {
    if (!searchArmed.current) {
      searchArmed.current = true;
      return;
    }
    const id = setTimeout(() => void loadList(), SEARCH_DELAY);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    // One open menu at a time across every row - and without a host the row
    // menus would not open at all.
    <PopHost>
      <PageHead title={t("subs.title")} sub={t("subs.subtitle")} />

      <SubsStats
        stats={stats}
        t={t}
        locale={loc}
        planFilter={planFilter}
        onPlanFilter={setPlanFilter}
      />

      <StripeStrip
        stripe={stripe}
        syncing={syncing}
        msg={syncMsg}
        onSync={() => void syncStripe()}
        t={t}
      />

      {/* No package dropdown here: the chips above are the package filter, they
          list the whole catalogue, and two controls writing one piece of state
          is how they end up disagreeing on screen. */}
      <Toolbar>
        <SearchField value={q} onChange={setQ} placeholder={t("search")} />
        <MenuField
          value={statusFilter}
          onChange={setStatusFilter}
          label={t("subs.col.status")}
          options={[
            { value: "all", label: t("all") },
            ...SUBSCRIPTION_STATUSES.map((s) => ({ value: s as string, label: t(`status.${s}`) })),
          ]}
        />
      </Toolbar>

      <SubsList
        rows={rows}
        loading={loading}
        t={t}
        locale={loc}
        onAssign={setAssigning}
        onRefund={setRefunding}
      />

      {refunding ? (
        <ConfirmModal
          title={t("subs.refundConfirmTitle")}
          body={fill(t("subs.refundConfirmBody"), { site: refunding.siteName })}
          confirmLabel={t("subs.refund")}
          cancelLabel={t("cancel")}
          onConfirm={() => doRefund()}
          onClose={() => setRefunding(null)}
        />
      ) : null}

      {assigning ? (
        <AssignModal
          api={api}
          locale={loc}
          row={assigning}
          t={t}
          planOptions={planOptions.map((p) => ({
            code: p.planCode,
            name: localeText(p.planName, loc) || p.planCode,
          }))}
          onClose={() => setAssigning(null)}
          onSaved={() => {
            setAssigning(null);
            void loadList();
            void loadStats();
          }}
        />
      ) : null}
    </PopHost>
  );
}
